import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { updateSEO } from '../../../utils/seo';

// ===== EN Membership — full redesign (mirrors JA membership; new design charter) =====
// Fees (¥30,000 / ¥60,000 / ¥150,000) match Home VII. English-only (no Japanese characters).

function NavyBand({ no, ja, en }: { no: string; ja: string; en: string }) {
  return (
    <div className="bg-[#1a2942] border-y border-[#a38e65]/25">
      <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-5 sm:py-[26px] flex items-center gap-4 sm:gap-6">
        <img src="/images/brand/jba-logo.png" alt="" aria-hidden="true" className="h-8 w-8 sm:h-9 sm:w-9 object-contain shrink-0 opacity-90" />
        <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl sm:text-[26px] tracking-[0.1em] shrink-0 leading-none">{no}</span>
        <h2 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-2xl lg:text-[26px] tracking-[0.06em] leading-tight">{ja}</h2>
        <span className="hidden md:block ml-auto font-['Playfair_Display',serif] italic text-[#a38e65] text-base lg:text-[19px] tracking-[0.12em]">{en}</span>
      </div>
    </div>
  );
}

export default function EnMembershipPage() {
  useEffect(() => {
    updateSEO({
      title: 'Membership Information | Japan Butler Association',
      description: 'Join Japan Butler Association. Choose from Regular, Associate, or Corporate membership. Enjoy exam discounts, seminar priority booking, exclusive events, and international exchange programs to advance your butler career.',
      keywords: 'membership, join, butler association, professional development, Japan Butler Association',
      canonicalUrl: '/en/membership'
    });
  }, []);

  const breadcrumbs = [
    { label: 'Home', href: '/en' },
    { label: 'Membership' }
  ];

  const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[46px] py-[18px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[46px] py-[18px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const values = [
    {
      en: 'Professional Growth', ja: 'Grow as a professional',
      img: '/images/generated/jba-fs-040-membership.webp',
      alt: 'Members learning practical techniques under a butler mentor',
      body: 'Through continuous educational opportunities and qualification support, you can build your skills as a butler in a systematic way.\n\nWe offer valuable chances to learn directly from directors at the forefront of the industry, and our seminars go beyond theory to share practical content grounded in real-world experience.',
      tags: ['Continuous education', 'Qualification support', 'Direct guidance from directors', 'Practical, hands-on learning'],
    },
    {
      en: 'Industry Network', ja: 'Build an industry network',
      img: '/images/generated/jba-fs-041-membership-v6.webp',
      alt: 'Members networking and exchanging information',
      body: 'Through exchange and information sharing with members across the country, you can build valuable connections. Meeting professionals who share your goals leads to new career opportunities and practical solutions.\n\nOnline study groups and networking events help you form ties that reach beyond your region.',
      tags: ['Nationwide member exchange', 'New career opportunities', 'Online study groups', 'Connections beyond region'],
    },
    {
      en: 'Cultural Contribution', ja: 'Contribute to butler culture',
      img: '/images/generated/jba-fs-042-membership-v3.webp',
      alt: 'Discussing the heritage of butler culture amid historic furnishings',
      body: 'Through the Association\'s activities, you can directly contribute to preserving and developing butler culture in Japan.\n\nYou take part in carrying over 130 years of tradition to the next generation, and in raising both the quality and the social recognition of the profession. Each member\'s work helps elevate the value of the butler craft.',
      tags: ['Carrying on 130 years of tradition', 'Raising industry quality', 'Wider social recognition', 'Elevating the profession'],
    },
  ];

  const planRows = [
    { key: 'Membership card', j: 'Digital membership card', s: 'Official membership card', c: 'Regular-member benefits for up to 5 staff' },
    { key: 'Seminar discount', j: '10% off', s: '20% off', c: 'Corporate seminar invitations' },
    { key: 'Exam discount', j: '10% off exam fees', s: '15% off exam fees', c: 'Custom corporate training consultation' },
    { key: 'Other benefits', j: 'Monthly newsletter / members-only content', s: 'Priority seminar booking / online study groups / voting rights', c: 'Priority job posting' },
  ];

  const plans = [
    {
      name: 'Associate Member', fee: '¥30,000', target: 'Those aspiring to become butlers, and those interested in butler culture',
      featured: false,
      benefits: ['Digital membership card', 'Monthly newsletter', '10% discount on seminars', '10% discount on exam fees', 'Email support (once per month)', 'Access to members-only content'],
    },
    {
      name: 'Full Member', fee: '¥60,000', target: 'Butler qualification holders and those with practical experience',
      featured: true,
      benefits: ['Official membership card', 'Monthly newsletter', '20% discount on seminars', '15% discount on exam fees', 'Priority email support (twice per month)', 'Priority booking for special seminars', 'Voting rights at general meetings', 'Access to online study groups'],
    },
    {
      name: 'Corporate Member', fee: '¥150,000', target: 'Hotels, restaurants, and other companies and organizations',
      featured: false,
      benefits: ['Regular-member benefits for up to 5 registered staff', 'Custom corporate training consultation', 'Priority job posting', 'Corporate special-seminar invitations', 'Additional registration (6th onward: ¥12,000 / person / year)'],
    },
  ];

  const steps = [
    { no: '01', title: 'Apply online', body: 'Complete the application form with the required details. It takes about five minutes, and you can apply easily from a smartphone.' },
    { no: '02', title: 'Review', body: 'We review your application and notify you of the result by email within one week. For full membership, we verify qualifications and practical experience.' },
    { no: '03', title: 'Begin your membership', body: 'After approval, we issue your membership card and you can use all benefits. Pay the first-year fee and begin your activities as a member.' },
  ];

  const audiences = [
    'Those aiming to obtain a butler qualification',
    'Those already active as butlers',
    'Those in guest service at hotels and restaurants',
    'Those considering a move into luxury service',
    'Those who want to raise their hospitality skills',
    'Those interested in butler culture and international etiquette',
    'Those who want to connect with like-minded peers',
    'Those who want to stay current with industry news',
  ];

  const faqs = [
    { q: 'Are there any requirements to join?', a: 'There are no special requirements for associate membership. Full membership requires practical experience as a butler or an Association-certified qualification. Corporate membership is for companies and organizations with legal-entity status.' },
    { q: 'When do I pay the annual fee?', a: 'You pay the first-year fee when you join, and from the following year renewal takes place every April. If you choose direct debit, payment is withdrawn automatically.' },
    { q: 'What if I join partway through the year?', a: 'The first-year fee is prorated by month. For example, if you join in October, you pay for the six months from October to March of the following year.' },
    { q: 'Can I withdraw at any time?', a: 'Yes, you may withdraw at any time. However, annual fees already paid are non-refundable. Please contact the office if you wish to withdraw.' },
    { q: 'When will my membership card arrive?', a: 'After your membership review is complete, your card will arrive in about two weeks. A digital membership card is available as soon as the review is complete.' },
    { q: 'When can I use seminar discounts?', a: 'You can use them from the moment your approval email arrives. Please provide your membership number when applying for a seminar.' },
  ];

  return (
    <Layout>
      {/* Hero */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[500px] lg:min-h-[580px] flex items-center">
        <img src="/images/generated/jba-p-014-mem-toast.webp" alt="Members gathering to discuss the development of butler culture" className="absolute inset-0 w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[700px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Membership</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(26px,3.2vw,42px)] leading-[1.42] tracking-[0.02em] mb-6">
              <span className="block">Build the future of</span>
              <span className="block">butler culture with us</span>
            </h1>
            <div className="space-y-5">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.9]">
                Since its founding in 1890, Japan Butler Association has made the preservation and development of butler culture its mission.
              </p>
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.9]">
                Becoming a member is more than a way to earn a qualification: it is taking part in the work of raising the professionalism of the butler craft and advancing the industry. Choose from three categories: Associate, Full, and Corporate.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* パンくず: ビルボード直後に配置（Yuichi 2026-07-17 指摘②）。共通Breadcrumbのヘッダー逃げpaddingを子セレクタで通常paddingへ上書き */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      {/* I. Why Join */}
      <NavyBand no="I" ja="Why members join us" en="Why Join" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mb-10 sm:mb-14 text-left">
            <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.01em] mb-5">
              Three kinds of value, <span className="text-[#a38e65]">from a single membership</span>
            </h3>
            <div className="space-y-5">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#333] leading-[1.9]">
                Through a selective membership system, we deliver high-quality service and learning to each member.
              </p>
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#333] leading-[1.9]">
                Growth as a professional, a network of like-minded peers, and a contribution to preserving butler culture: the time you spend as a member becomes a solid environment that guides your career to the next stage.
              </p>
            </div>
          </div>
          <div className="space-y-10 sm:space-y-14">
            {values.map((v, i) => (
              <div key={v.en} className="grid lg:grid-cols-2 items-center gap-8 lg:gap-14 border border-[#e2ddd2] overflow-hidden">
                <div className={`relative min-h-[260px] lg:min-h-[360px] bg-black ${i % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <img src={v.img} alt={v.alt} className="absolute inset-0 w-full h-full object-cover object-top" />
                </div>
                <div className={`px-7 sm:px-10 lg:px-12 py-10 lg:py-12 text-left ${i % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm tracking-[0.06em] mb-3">{v.en}</span>
                  <h4 className="jba-display font-medium text-[#1a2942] text-xl sm:text-2xl md:text-[26px] leading-[1.4] tracking-[0.01em] mb-4">{v.ja}</h4>
                  <div className="space-y-4 mb-6">{v.body.split(/\n{2,}/).map((s, i) => (
                    <p key={i} className="font-['Noto_Serif_JP',serif] text-[#333] text-sm sm:text-base leading-[1.9]">{s.trim()}</p>
                  ))}</div>
                  <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                    {v.tags.map((t) => (
                      <li key={t} className="flex items-start gap-2.5 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638]"><span className="text-[#a38e65] text-[10px] mt-[6px]">◆</span><span>{t}</span></li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* II. Plans & Fees */}
      <NavyBand no="II" ja="Categories and annual fees" en="Plans & Fees" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-12 sm:mb-16">
            <div className="order-2 lg:order-1 text-left">
              <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.01em] mb-5">
                Three categories, <span className="text-[#a38e65]">chosen to fit your goals</span>
              </h3>
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#333] leading-[1.9] mb-4">
                Registration is free for every category. We offer three tiers — Associate, Full, and Corporate — with benefits such as qualification support, seminar privileges, and networking suited to each.
              </p>
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#333] leading-[1.9]">
                The fees reflect the professionalism and social significance of the butler craft. They support the Association management and member services befitting professionals who deliver high-level service to affluent and VIP clients.
              </p>
            </div>
            <div className="order-1 lg:order-2">
              <img src="/images/salon0926/20260912_105941.jpg" alt="The window-side sofa and fireplace in the butler salon in daylight" className="w-full h-[300px] sm:h-[400px] md:h-[440px] object-cover border border-[#a38e65]/30" />
            </div>
          </div>

          <div className="border-t-2 border-[#1a2942] overflow-x-auto">
            <table className="w-full border-collapse min-w-[720px]">
              <thead>
                <tr className="border-b border-[#e2ddd2]">
                  <th className="text-left py-5 pr-4 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em] font-medium align-bottom w-[24%]">Category</th>
                  <th className="text-left py-5 px-4 align-bottom">
                    <div className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl">Associate</div>
                    <div className="font-['Playfair_Display',serif] text-[#a38e65] text-2xl sm:text-[26px] mt-1">¥30,000<span className="text-xs text-[#8f7a56] ml-1">/yr</span></div>
                  </th>
                  <th className="text-left py-5 px-4 align-bottom bg-[#f8f6f0]">
                    <div className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl">Full</div>
                    <div className="font-['Playfair_Display',serif] text-[#a38e65] text-2xl sm:text-[26px] mt-1">¥60,000<span className="text-xs text-[#8f7a56] ml-1">/yr</span></div>
                  </th>
                  <th className="text-left py-5 px-4 align-bottom">
                    <div className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl">Corporate</div>
                    <div className="font-['Playfair_Display',serif] text-[#a38e65] text-2xl sm:text-[26px] mt-1">¥150,000<span className="text-xs text-[#8f7a56] ml-1">/yr</span></div>
                  </th>
                </tr>
              </thead>
              <tbody className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638]">
                {planRows.map((r) => (
                  <tr key={r.key} className="border-b border-[#e2ddd2]">
                    <td className="py-4 pr-4 text-[#1a2942] font-medium">{r.key}</td>
                    <td className="py-4 px-4">{r.j}</td>
                    <td className="py-4 px-4 bg-[#f8f6f0]">{r.s}</td>
                    <td className="py-4 px-4">{r.c}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-10 mt-12 sm:mt-14">
            {plans.map((p) => (
              <div key={p.name} className={`flex flex-col text-left border ${p.featured ? 'bg-[#1a2942] border-[#a38e65]' : 'bg-white border-[#e2ddd2]'}`}>
                <div className={`px-7 sm:px-8 py-7 border-b ${p.featured ? 'border-[#a38e65]/40' : 'border-[#e2ddd2]'}`}>
                  {p.featured && <span className="inline-block font-['Playfair_Display',serif] italic text-[#d7b779] text-xs tracking-[0.14em] mb-2">Most Popular</span>}
                  <h4 className={`jba-display font-medium text-xl sm:text-2xl mb-2 ${p.featured ? 'text-[#f8f6f0]' : 'text-[#1a2942]'}`}>{p.name}</h4>
                  <div className={`font-['Playfair_Display',serif] text-3xl sm:text-[32px] leading-none ${p.featured ? 'text-[#d7b779]' : 'text-[#a38e65]'}`}>{p.fee}<span className={`text-sm ml-1 ${p.featured ? 'text-[#9d9686]' : 'text-[#8f7a56]'}`}>/yr</span></div>
                  <div className={`font-['Noto_Serif_JP',serif] text-xs mt-1 ${p.featured ? 'text-[#9d9686]' : 'text-[#8f7a56]'}`}>Registration: free</div>
                </div>
                <div className="px-7 sm:px-8 py-7 flex flex-col flex-1">
                  <p className={`font-['Noto_Serif_JP',serif] text-sm mb-5 leading-[1.8] ${p.featured ? 'text-[#e7d9b6]' : 'text-[#8f7a56]'}`}>For: {p.target}</p>
                  <ul className="space-y-0">
                    {p.benefits.map((b) => (
                      <li key={b} className={`flex items-start gap-3 py-2.5 border-b font-['Noto_Serif_JP',serif] text-sm ${p.featured ? 'border-[#a38e65]/25 text-[#ded8cc]' : 'border-[#e2ddd2] text-[#4a4638]'}`}><span className="text-[#a38e65] text-[10px] mt-[7px]">◆</span><span>{b}</span></li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* III. How to Join */}
      <NavyBand no="III" ja="Joining takes just three steps" en="How to Join" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-[1fr_360px] gap-10 lg:gap-16 items-start">
            <div>
              <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.01em] mb-8">
                From application to membership, <span className="text-[#a38e65]">in as little as five minutes</span>
              </h3>
              <div className="border-t border-[#e2ddd2]">
                {steps.map((s) => (
                  <div key={s.no} className="flex gap-5 sm:gap-7 border-b border-[#e2ddd2] py-7">
                    <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl sm:text-4xl leading-none shrink-0 pt-1">{s.no}</span>
                    <div>
                      <h4 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-2 leading-[1.5]">{s.title}</h4>
                      <div className="space-y-4">{s.body.split(/\n{2,}/).map((s, i) => (
                        <p key={i} className="font-['Noto_Serif_JP',serif] text-sm sm:text-base text-[#4a4638] leading-[1.85]">{s.trim()}</p>
                      ))}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:pt-2">
              <img src="/images/generated/jba-fs-en-008-membership.webp" alt="Association staff guiding a visitor about joining" className="w-full h-64 lg:h-[420px] object-cover border border-[#a38e65]/30 mb-5" />
              <a href="/en/contact" className={`${BTN08_ON_LIGHT} w-full`}>Ask about joining</a>
            </div>
          </div>
        </div>
      </section>

      {/* IV. Who It's For */}
      <NavyBand no="IV" ja="Chosen by people like these" en="Who It's For" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.01em] mb-8">
                An entry point for both <span className="text-[#a38e65]">learning and practice</span>
              </h3>
              <ul className="border-t border-[#e2ddd2]">
                {audiences.map((a) => (
                  <li key={a} className="flex items-start gap-3.5 border-b border-[#e2ddd2] py-3.5 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#333] leading-[1.7]"><span className="text-[#a38e65] mt-[3px]"><i className="ri-check-line"></i></span><span>{a}</span></li>
                ))}
              </ul>
            </div>
            <div className="order-1 lg:order-2">
              <img src="/images/generated/jba-fs-045-membership-v6.webp" alt="A member of the Association with a composed, dignified presence" className="w-full h-[320px] sm:h-[420px] lg:h-[520px] object-cover object-top border border-[#a38e65]/30" />
            </div>
          </div>
        </div>
      </section>

      {/* V. Payment & Renewal */}
      <NavyBand no="V" ja="Payment and annual renewal" en="Payment & Renewal" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mb-10 text-left">
            <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.01em] mb-5">
              A payment structure <span className="text-[#a38e65]">you can rely on</span>
            </h3>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#333] leading-[1.9]">
              We offer three ways to pay your annual fee — credit card, bank transfer, and direct debit — so you can choose what suits you best.
            </p>
          </div>
          <div className="grid md:grid-cols-[1.4fr_1fr] gap-8 lg:gap-12 items-start">
            <dl className="border-t-2 border-[#1a2942]">
              <div className="flex flex-col sm:flex-row gap-1 sm:gap-8 border-b border-[#e2ddd2] py-5">
                <dt className="sm:w-[150px] sm:shrink-0 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.04em] pt-0.5">Credit card</dt>
                <dd className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.8]">VISA, MasterCard, JCB, and American Express are accepted.</dd>
              </div>
              <div className="flex flex-col sm:flex-row gap-1 sm:gap-8 border-b border-[#e2ddd2] py-5">
                <dt className="sm:w-[150px] sm:shrink-0 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.04em] pt-0.5">Bank transfer</dt>
                <dd className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.8]">Transfer to our designated account. Transfer fees are the member&apos;s responsibility.</dd>
              </div>
              <div className="flex flex-col sm:flex-row gap-1 sm:gap-8 border-b border-[#e2ddd2] py-5">
                <dt className="sm:w-[150px] sm:shrink-0 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.04em] pt-0.5">Direct debit</dt>
                <dd className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.8]">Automatic withdrawal can be set up from the second year onward.</dd>
              </div>
            </dl>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-7 sm:p-8">
              <span className="block font-['Playfair_Display',serif] italic text-[#a38e65] text-sm tracking-[0.06em] mb-2">Renewal</span>
              <h4 className="jba-display font-medium text-[#f8f6f0] text-lg mb-3">Annual renewal</h4>
              <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.9]">Renewal is every April. The first year is calculated from the month you join, with annual renewal in April thereafter. Mid-year joins are prorated by month for the first year.</p>
            </div>
          </div>
        </div>
      </section>

      {/* VI. Before You Join */}
      <NavyBand no="VI" ja="Please read before you join" en="Before You Join" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            <div className="border border-[#e2ddd2] p-7 sm:p-9 text-left">
              <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm tracking-[0.06em] mb-2">Membership</span>
              <h4 className="jba-display font-medium text-[#1a2942] text-xl mb-4">About the membership system</h4>
              <ul className="space-y-0">
                {['Member benefits will be expanded in stages.', 'Some benefits will begin as soon as they are ready.', 'Benefit details may change without notice.'].map((t) => (
                  <li key={t} className="flex items-start gap-3 border-b border-[#e2ddd2] py-3 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.8]"><span className="text-[#a38e65] text-[10px] mt-[7px]">◆</span><span>{t}</span></li>
                ))}
              </ul>
            </div>
            <div className="border border-[#e2ddd2] p-7 sm:p-9 text-left">
              <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm tracking-[0.06em] mb-2">Qualification</span>
              <h4 className="jba-display font-medium text-[#1a2942] text-xl mb-4">About membership qualifications</h4>
              <ul className="space-y-0">
                {['Membership does not guarantee employment or income.', 'Earning a qualification depends on your own effort and study.', 'Fees are non-refundable, including for mid-year withdrawal.'].map((t) => (
                  <li key={t} className="flex items-start gap-3 border-b border-[#e2ddd2] py-3 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.8]"><span className="text-[#a38e65] text-[10px] mt-[7px]">◆</span><span>{t}</span></li>
                ))}
              </ul>
            </div>
          </div>
          <div className="bg-[#f8f6f0] border border-[#a38e65]/30 p-7 sm:p-9 mt-8 text-left">
            <h4 className="jba-display font-medium text-[#1a2942] text-lg mb-3">A selective management approach</h4>
            <p className="font-['Noto_Serif_JP',serif] text-sm sm:text-base text-[#4a4638] leading-[1.9]">
              With a selective management structure, we place value on providing high-quality service to each member. We offer the attentive, personal support and care that is difficult to achieve at a large-scale association.
            </p>
          </div>
        </div>
      </section>

      {/* VII. FAQ */}
      <NavyBand no="VII" ja="Frequently asked questions" en="FAQ" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mx-auto border-t border-[#e2ddd2]">
            {faqs.map((f) => (
              <div key={f.q} className="border-b border-[#e2ddd2] py-6">
                <h4 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl leading-[1.5] mb-3 flex items-start gap-3">
                  <span className="font-['Playfair_Display',serif] text-[#a38e65] text-xl leading-none pt-0.5">Q.</span><span>{f.q}</span>
                </h4>
                <p className="font-['Noto_Serif_JP',serif] text-sm sm:text-base text-[#4a4638] leading-[1.9] pl-8">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 md:p-14 text-center">
            <h3 className="jba-display font-medium text-2xl sm:text-3xl text-[#f8f6f0] tracking-[0.02em]">Join today and become part of butler culture</h3>
            <div className="w-11 h-px bg-[#a38e65] mx-auto mt-4 mb-6"></div>
            <p className="text-base sm:text-lg text-[#e7e2d8] mb-8 font-['Noto_Serif_JP',serif] max-w-2xl mx-auto leading-[1.85]">Applying is simple through our online form. If anything is unclear, please feel free to get in touch.</p>
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
              <a href="/en/contact" className={BTN08_ON_NAVY}>Ask about joining</a>
              <a href="/en/qualifications" className={BTN08_ON_NAVY}>View certifications</a>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
